/**
 * Plays a recorded event log back against the recording's own clock, at a chosen speed.
 *
 * Position is measured in recording milliseconds from the first event. At speed `s`, one real millisecond moves
 * the position `s` milliseconds, so a 32-minute run takes 32 minutes at 1x and 8 at 4x. An optional gap cap
 * shortens long quiet stretches (useful for video); it is off unless asked for, so 1x means real time.
 */
import { PlatformEventSchema, type PlatformEvent } from "@botallion/contracts";

export interface Timer {
  now(): number;
  set(fn: () => void, ms: number): unknown;
  clear(handle: unknown): void;
}

export const realTimer: Timer = {
  now: () => performance.now(),
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
};

export interface ReplayOptions {
  /** Called with the next events due. `reset: true` means start from an empty state first (a backwards seek). */
  onApply: (events: PlatformEvent[], reset: boolean) => void;
  /** Called when playback starts, pauses, finishes or the speed changes. */
  onState?: () => void;
  timer?: Timer;
}

/** Recording offsets (ms from the first event) for each event, with gaps longer than `capMs` shortened to `capMs`. */
export function timeline(events: readonly PlatformEvent[], capMs: number | null): number[] {
  const out: number[] = [];
  let offset = 0;
  for (let i = 0; i < events.length; i++) {
    if (i > 0) {
      const gap = Math.max(0, Date.parse(events[i]!.ts) - Date.parse(events[i - 1]!.ts));
      offset += capMs === null ? gap : Math.min(gap, capMs);
    }
    out.push(offset);
  }
  return out;
}

/** Read a recording: a PlatformEvent array, or `{ events: [...] }` (the contracts demo's `--json` output). */
export function parseRecording(input: unknown): PlatformEvent[] {
  const raw = Array.isArray(input) ? input : input && typeof input === "object" && Array.isArray((input as { events?: unknown }).events) ? (input as { events: unknown[] }).events : null;
  if (!raw) throw new Error("A recording is a JSON array of PlatformEvents, or an object with an events array.");
  const events = raw.map((e, i) => {
    const parsed = PlatformEventSchema.safeParse(e);
    if (!parsed.success) throw new Error(`Event ${i + 1} is not a valid PlatformEvent.`);
    return parsed.data;
  });
  return events.sort((a, b) => a.seq - b.seq);
}

export class ReplayPlayer {
  readonly events: readonly PlatformEvent[];
  private offsets: number[];
  private capMs: number | null;
  private readonly timer: Timer;
  private readonly opts: ReplayOptions;
  /** Index of the next event to apply. */
  private next = 0;
  /** Position when playback last (re)started, and the real time it did. */
  private basePos = 0;
  private baseReal = 0;
  private handle: unknown = null;
  private _speed = 1;
  private _playing = false;

  constructor(events: readonly PlatformEvent[], opts: ReplayOptions & { speed?: number; capMs?: number | null }) {
    this.events = events;
    this.opts = opts;
    this.timer = opts.timer ?? realTimer;
    this.capMs = opts.capMs ?? null;
    this.offsets = timeline(events, this.capMs);
    this._speed = opts.speed ?? 1;
  }

  get speed(): number {
    return this._speed;
  }
  get playing(): boolean {
    return this._playing;
  }
  get applied(): number {
    return this.next;
  }
  get finished(): boolean {
    return this.next >= this.events.length;
  }
  /** Length of the recording in recording ms (after any gap cap). */
  get duration(): number {
    return this.offsets.at(-1) ?? 0;
  }
  /** Recording offset (ms from the first event) of event `i`, after any gap cap. */
  offsetAt(i: number): number {
    return this.offsets[Math.max(0, Math.min(this.offsets.length - 1, i))] ?? 0;
  }
  /** Current position in recording ms. */
  get position(): number {
    if (!this._playing) return this.basePos;
    return Math.min(this.duration, this.basePos + (this.timer.now() - this.baseReal) * this._speed);
  }
  /** The recording's own clock at the current position, for "how long ago" on screen. */
  get recordedNow(): number {
    const first = this.events[0];
    if (!first) return Date.now();
    const last = this.events[Math.max(0, this.next - 1)];
    // Between events, interpolate from the last applied one so heartbeats age smoothly.
    const sinceLast = this.position - (this.offsets[Math.max(0, this.next - 1)] ?? 0);
    return Date.parse((last ?? first).ts) + Math.max(0, sinceLast);
  }

  play(): void {
    if (this._playing) return;
    if (this.finished) this.seek(0);
    this._playing = true;
    this.baseReal = this.timer.now();
    this.schedule();
    this.opts.onState?.();
  }

  pause(): void {
    if (!this._playing) return;
    this.basePos = this.position;
    this._playing = false;
    this.cancel();
    this.opts.onState?.();
  }

  setSpeed(speed: number): void {
    if (!(speed > 0)) throw new Error("speed must be positive");
    this.basePos = this.position;
    this.baseReal = this.timer.now();
    this._speed = speed;
    if (this._playing) this.schedule();
    this.opts.onState?.();
  }

  /** Shorten gaps longer than `capMs` (null for real timing). Keeps the current event as the position. */
  setGapCap(capMs: number | null): void {
    const at = this.next > 0 ? this.next - 1 : -1;
    this.capMs = capMs;
    this.offsets = timeline(this.events, capMs);
    this.basePos = at >= 0 ? this.offsets[at]! : 0;
    this.baseReal = this.timer.now();
    if (this._playing) this.schedule();
    this.opts.onState?.();
  }

  /** Jump to a recording position. Backwards rebuilds from the start. */
  seek(position: number): void {
    const target = Math.max(0, Math.min(this.duration, position));
    let upTo = 0;
    while (upTo < this.events.length && this.offsets[upTo]! <= target) upTo++;
    if (upTo < this.next) {
      this.next = 0;
      this.opts.onApply(this.events.slice(0, upTo) as PlatformEvent[], true);
    } else if (upTo > this.next) {
      this.opts.onApply(this.events.slice(this.next, upTo) as PlatformEvent[], false);
    }
    this.next = upTo;
    this.basePos = target;
    this.baseReal = this.timer.now();
    if (this._playing) this.schedule();
    this.opts.onState?.();
  }

  /** Apply the next event (and any sharing its moment) and pause there. */
  step(): void {
    if (this.finished) return;
    this.pause();
    this.seek(this.offsets[this.next]!);
  }

  /** Go back to the moment before the current one and pause there. The first moment is as far back as it goes. */
  stepBack(): void {
    if (this.next === 0) return;
    this.pause();
    let i = this.next - 1;
    const current = this.offsets[i]!;
    while (i > 0 && this.offsets[i - 1] === current) i--;
    this.seek(i > 0 ? this.offsets[i - 1]! : 0);
  }

  stop(): void {
    this.pause();
  }

  private cancel(): void {
    if (this.handle !== null) this.timer.clear(this.handle);
    this.handle = null;
  }

  private schedule(): void {
    this.cancel();
    this.flush();
    if (this.finished) {
      this.basePos = this.duration;
      this._playing = false;
      this.opts.onState?.();
      return;
    }
    const wait = Math.max(0, (this.offsets[this.next]! - this.position) / this._speed);
    this.handle = this.timer.set(() => {
      this.handle = null;
      this.schedule();
    }, wait);
  }

  /**
   * Apply every event whose offset the position has reached. The tolerance absorbs floating-point drift between
   * the scheduled wait and the clock: without it, a timer can fire a hair short of an event and reschedule a wait
   * too small to move the clock, forever.
   */
  private flush(): void {
    const pos = this.position + 1e-6;
    const from = this.next;
    while (this.next < this.events.length && this.offsets[this.next]! <= pos) this.next++;
    if (this.next > from) this.opts.onApply(this.events.slice(from, this.next) as PlatformEvent[], false);
  }
}
